/**
 * React Query hooks for the wiberry Sync Agent UI-management surface (#3071).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { wiberryAgentService } from '../services/wiberryAgentService';
import { revokeClient } from '../services/devicePairingService';

export const wiberryAgentKeys = {
  all: ['wiberryAgent'] as const,
  runs: () => [...wiberryAgentKeys.all, 'runs'] as const,
  status: () => [...wiberryAgentKeys.all, 'status'] as const,
};

/**
 * Outstanding enrolment tokens. Never carries a plaintext token — see
 * `useGenerateWiberryToken` for the one-time-shown value.
 *
 * Polls every 30s (codex P2, #3071): a token generated here is consumed
 * server-side the moment the farmer runs the install command — if this
 * query only refetched on remount/focus, a just-consumed token would keep
 * showing under "Ausstehende Einladungen" while the agent already appears
 * in the (also-polling) agents list, and its stale "Verwerfen" action would
 * 404. Same 30s cadence as `useWiberryAgents` so both lists settle together.
 */

/**
 * Connected agents. Polls every 30s (mirrors `useIntegrations`'s idiom) so a
 * revoke performed elsewhere (or an agent's first successful sync) reflects
 * promptly without a manual refresh.
 */

/**
 * Recent sync runs (#3071 pass 2 — see `wiberryAgentService.listRuns`'s
 * docstring). `enabled` (default `true`) lets a caller skip the request
 * entirely — `WiberryAgentPanel` passes `agents.length > 0` (codex P3,
 * #3071): with no connected agent, the runs section never renders, so
 * firing this read anyway would be pure avoidable traffic against a path
 * with no live backend handler yet, on every single Integrationen page
 * visit for the (currently universal) no-agent-yet tenant.
 */
export function useWiberryRuns(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: wiberryAgentKeys.runs(),
    queryFn: () => wiberryAgentService.listRuns(),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Stable key so `WiberryAgentPanel` can find-and-remove exactly this
 * mutation's `MutationCache` entry on dialog close — see
 * `useGenerateWiberryToken`'s docstring for why `.reset()` alone isn't
 * enough.
 */

/**
 * Generate a new enrolment token. The mutation's `data` (which carries the
 * ONE plaintext token) lives only in the mutation's own transient result —
 * callers must read it from `onSuccess`/`.data` into local component state.
 *
 * `.reset()` is NOT sufficient on its own to make the plaintext
 * unrecoverable (codex P1, #3071): it only detaches the calling hook's own
 * observer from the mutation and clears what THAT observer reports — the
 * underlying `Mutation` object stays in React Query's `MutationCache`,
 * `data` (the plaintext) and all, until its `gcTime` elapses (default 5
 * minutes) or something explicitly removes it. `gcTime: 0` here shortens
 * that window to "as soon as no observer is attached", and the caller
 * (`WiberryAgentPanel.handleCloseDialog`) additionally calls
 * `queryClient.getMutationCache().remove(...)` on close for an immediate,
 * deterministic guarantee rather than relying on GC timing (FR-016.1).
 */

/**
 * S9 — the single integration's live status.
 *
 * Polls on the same 30s cadence as the agents/tokens lists so the heartbeat
 * badge, the agents table and the runs table never disagree on screen. The
 * badge is the one tile where staleness is itself the bug being reported: an
 * "online" dot that is five minutes old is precisely the false reassurance S9
 * exists to remove.
 */
export function useIntegrationStatus(source = 'wiberry') {
  return useQuery({
    queryKey: [...wiberryAgentKeys.status(), source],
    queryFn: () => wiberryAgentService.getStatus(source),
    refetchInterval: 30_000,
  });
}

/**
 * Disconnect the tenant's paired client (S10 — replaces `useRevokeWiberryAgent`,
 * which called v1's deleted `/integrations/agent/agents/{id}`).
 */
export function useRevokeIntegrationClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => revokeClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wiberryAgentKeys.status() });
      queryClient.invalidateQueries({ queryKey: wiberryAgentKeys.runs() });
    },
  });
}
